import { useMemo } from "react";
import { Scribble } from "shared/types/scribble";
import { Buildings, User } from "@phosphor-icons/react";
import { Avatar } from "@heroui/react";
import { useUsers } from "shared/contexts/users.context";

interface NotesListProps {
  notes: Scribble[];
  selectedNote: Scribble | null;
  onSelectNote: (note: Scribble) => void;
}

interface UserInfo {
  imageUrl: string;
  name: string;
  firstName: string;
}

export const NotesList: React.FC<NotesListProps> = ({
  notes,
  selectedNote,
  onSelectNote
}) => {
  const { users } = useUsers();

  // Memoize user lookups for performance
  const userMap = useMemo(() => {
    if (!users || users.length === 0) return {};

    return users.reduce((map, user) => {
      map[user.id] = {
        imageUrl: user?.image_url || '',
        name: `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Onbekend',
        firstName: user?.first_name || 'Onbekend'
      };
      return map;
    }, {} as Record<string, UserInfo>);
  }, [users]);

  const getUserInfo = (userId: string): UserInfo => {
    return userMap[userId] || {
      imageUrl: '',
      name: 'Onbekend',
      firstName: 'Onbekend'
    };
  };
  const stripHtml = (html: string) => {
    const tmp = document.createElement("DIV");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  const formatDate = (date: Date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      const hours = d.getHours();
      const minutes = d.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } else if (days === 1) {
      return 'Gisteren';
    } else if (days < 7) {
      return `${days}d geleden`;
    } else {
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    }
  };

  return (
    <div className="flex flex-col overflow-y-auto">
      {notes.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-8 text-neutral-primary/50">
          <p className="text-sm">Geen notities gevonden</p>
        </div>
      ) : (
        notes.map((note) => {
          const userInfo = getUserInfo(note.userId);

          return (
            <button
              key={note.id}
              onClick={() => onSelectNote(note)}
              className={`flex flex-col gap-1 p-3 text-left border-b border-neutral-primary/5 transition-colors ${
                selectedNote?.id === note.id
                  ? 'bg-moss-primary/5'
                  : 'bg-white hover:bg-neutral-primary/3'
              }`}
            >
              {/* User info and date header */}
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Avatar
                    src={userInfo.imageUrl}
                    alt={userInfo.name}
                    className="w-5 h-5"
                    classNames={{
                      base: "w-5 h-5 min-w-[20px]",
                      img: "w-5 h-5"
                    }}
                  />
                  <span className="text-xs text-neutral-primary/50">
                    {userInfo.firstName}
                  </span>
                </div>
                <span className="text-xs text-neutral-primary/40">
                  {formatDate(note.updatedAt)}
                </span>
              </div>

              {/* Title */}
              <h3 className={`font-medium text-sm truncate ${
                selectedNote?.id === note.id ? 'text-neutral-primary' : 'text-neutral-primary/90'
              }`}>
                {note.title || 'Naamloos'}
              </h3>

              {/* Content preview */}
              <p className="text-xs text-neutral-primary/50 line-clamp-2">
                {stripHtml(note.content || '')}
              </p>

              {/* Badges and tags */}
              {(note.tags || note.building || (note.contacts && note.contacts.length > 0) || note.renovationProject) && (
                <div className="flex items-center gap-1 mt-1 flex-wrap">
                  {/* Renovation project badge */}
                  {note.renovationProject && (
                    <span className="px-1.5 py-0.5 bg-moss-primary/10 text-moss-primary text-xs rounded inline-flex items-center gap-1">
                      RENO-{note.renovationProject.id}
                    </span>
                  )}

                  {/* Building badge */}
                  {note.building && (
                    <span className="px-1.5 py-0.5 bg-neutral-primary/10 text-neutral-primary/70 text-xs rounded inline-flex items-center gap-1">
                      <Buildings size={10} weight="duotone" />
                      {note.building.id}
                    </span>
                  )}

                  {/* Contact names */}
                  {note.contacts && note.contacts.length > 0 && note.contacts.map((contact, idx) => (
                    <span key={idx} className="px-1.5 py-0.5 bg-blue-500/10 text-blue-700 text-xs rounded inline-flex items-center gap-1">
                      <User size={10} weight="duotone" />
                      {contact.firstName} {contact.lastName}
                    </span>
                  ))}

                  {/* Tags */}
                  {note.tags && note.tags.split(',').map(tag => tag.trim()).filter(Boolean).map(tag => (
                    <span key={tag} className="text-xs text-moss-primary/70">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </button>
          );
        })

      )}
    </div>
  );
};
